/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useRef } from 'react';

// Styles
import styles from './TimePicker.module.css';

// Components
import Picto from '../Picto/Picto';

/**
 * TimePicker component - Time picker for selecting hours and minutes
 * @param {object} props
 * @param {string} [props.value] - Initial time (HH:MM format)
 * @param {(time: string) => void} [props.onChange] - Callback when time changes
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} TimePicker component
 */
function TimePicker({ value = '00:00', onChange, className }) {
  const inputRef = useRef(null);

  const [hours, minutes] = value.split(':').map(Number);

  /**
   * Update the time
   * @param {number} h
   * @param {number} m
   */
  const updateTime = (h, m) => {
    const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    onChange?.(formatted);
  };

  const incrementHours = () => updateTime((hours + 1) % 24, minutes);
  const decrementHours = () => updateTime((hours + 23) % 24, minutes);
  const incrementMinutes = () => updateTime(hours, (minutes + 1) % 60);
  const decrementMinutes = () => updateTime(hours, (minutes + 59) % 60);

  /**
   * Handle native time change
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleNativeChange = (e) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={`${styles['time-picker-container']} ${className || ''}`}>
      <input
        ref={inputRef}
        type="time"
        value={value}
        onChange={handleNativeChange}
        className={styles['hidden-input']}
      />

      <div
        role="button"
        tabIndex={0}
        className={styles['time-picker-ui']}
        onClick={() => inputRef.current?.showPicker?.()}
      >
        {/* Hours */}
        <div className={styles['time-unit']}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); incrementHours(); }}
            className={styles['time-unit-button']}
          >
            <Picto icon="chevron-up-range" />
          </button>
          <span className={styles['time-digit']}>{String(hours).padStart(2, '0')}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); decrementHours(); }}
            className={styles['time-unit-button']}
          >
            <Picto icon="chevron-down-range" />
          </button>
        </div>

        <span className={styles['time-separator']}>:</span>

        {/* Minutes */}
        <div className={styles['time-unit']}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); incrementMinutes(); }}
            className={styles['time-unit-button']}
          >
            <Picto icon="chevron-up-range" />
          </button>
          <span className={styles['time-digit']}>{String(minutes).padStart(2, '0')}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); decrementMinutes(); }}
            className={styles['time-unit-button']}
          >
            <Picto icon="chevron-down-range" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default TimePicker;
